// All imports at the top — strict ESM order required
import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from './lib/firebase';
import { Layout } from './components/Layout';
import { About } from './pages/About';
import { Privacy } from './pages/Privacy';
import { Contact } from './pages/Contact';
import { Login } from './pages/Login';
import { Loader2 } from 'lucide-react';

// Heavy components — code-split after all imports
const Dashboard = lazy(() =>
    import('./components/Dashboard').then(m => ({ default: m.Dashboard }))
);
const Reader = lazy(() =>
    import('./pages/Reader').then(m => ({ default: m.Reader }))
);
const Account = lazy(() =>
    import('./pages/Account').then(m => ({ default: m.Account }))
);

// Reused loading spinner
export const Spinner = ({ label = 'Loading...' }: { label?: string }) => (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center gap-3">
        <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
        <p className="text-slate-500 text-sm">{label}</p>
    </div>
);

// Suspense wrapper for lazy-loaded route elements.
// NOTE: Suspense must wrap the *element*, NOT the <Route> components themselves.
// React Router v6 requires direct Route children — inserting any non-Route node
// (like <Suspense>) between parent and child Routes breaks route resolution
// and causes the router.js crash.
const SuspenseRoute = ({ element }: { element: React.ReactNode }) => (
    <Suspense fallback={<Spinner label="Loading page..." />}>
        {element}
    </Suspense>
);

// Home page — Upload interface
import { Home } from './pages/Home';

function AppContent() {
    // undefined = resolving auth, null = not signed in, User = signed in
    const [user, setUser] = useState<User | null | undefined>(undefined);

    useEffect(() => {
        let unsubscribe: (() => void) | undefined;

        try {
            unsubscribe = onAuthStateChanged(
                auth,
                (currentUser) => {
                    setUser(currentUser ?? null);
                },
                (error) => {
                    const authErr = error as any;
                    console.warn('[Auth] Authorization issue:', authErr.code, authErr.message);
                    setUser(null);
                }
            );
        } catch (err: any) {
            console.warn('[Auth] Init error — continuing as guest:', err?.message);
            setUser(null);
        }

        return () => { if (unsubscribe) unsubscribe(); };
    }, []);

    // Block ALL route rendering until Firebase confirms auth state.
    if (user === undefined) {
        return <Spinner label="Initializing secure session..." />;
    }

    return (
        <Routes>
            {/* Public route */}
            <Route
                path="/login"
                element={user ? <Navigate to="/library" replace /> : <Login />}
            />

            {/* Guest: all routes redirect to login */}
            {!user ? (
                <Route path="*" element={<Navigate to="/login" replace />} />
            ) : (
                // Authenticated routes
                <Route element={<Layout />}>
                    <Route path="/" element={<SuspenseRoute element={<Home />} />} />
                    <Route path="/library" element={<SuspenseRoute element={<Dashboard />} />} />
                    <Route path="/account" element={<SuspenseRoute element={<Account />} />} />
                    <Route path="/about" element={<SuspenseRoute element={<About />} />} />
                    <Route path="/privacy" element={<SuspenseRoute element={<Privacy />} />} />
                    <Route path="/contact" element={<SuspenseRoute element={<Contact />} />} />
                    <Route path="/reader/:bookId" element={<SuspenseRoute element={<Reader />} />} />
                    {/* Default redirect: after login → Library */}
                    <Route path="*" element={<Navigate to="/library" replace />} />
                </Route>
            )}
        </Routes>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <AppContent />
        </BrowserRouter>
    );
}
